import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TreeItem, { TreeItemProps } from '@mui/lab/TreeItem';
import TreeView from '@mui/lab/TreeView';
import { Grid, Stack } from '@mui/material';
import { styled } from '@mui/styles';
import { isEmpty } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import {
  getCandidateInteractionResultPipelines,
  ICandidateInteractionActionPipeline,
  saveCandidateInteractionResultPipelines,
  resetDefaultCandidateInteractionResultPipelines,
} from 'wisestep-api';
import { WsButton, Label, ControlledTextInput, WsColors } from 'wisestep-base';

import { SettingsPageLayout } from '../../../components/SettingsPageLayout';
import { useWorkspaceContext } from '../../../context';
import {
  getSettingBreadCrumbList,
  ISettingBreadCrumbTypes,
  Pages,
} from '../../../features/teams/helper/defaultValues';
import { AtsCustomAPIAccess, useAtsCustomAPIAccess } from '../../../hooks/useAtsCustomAPIAccess';
import { warning, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import HeaderBox from '../components/HeaderBox';
import { DeleteIconButton, EditIconButton, SaveIconButton } from '../components/StyledIconButtons';
import AddActionButtonDialog from '../ManageRecruiterActions/AddActionButtonDialog';
import ConfirmationDialog from '../ManageRecruiterActions/ConfirmationDialog';
import { CustomContent } from '../ManageRecruiterActions/CustomTreeComponent';
import {
  action,
  buildNewItem,
  candidateInteractionMessagingPipelines,
  idArrayGenerator,
} from '../ManageRecruiterActions/helper';

type Size = {
  fontSize: number;
};

const SmallLabel = styled(Label)(({ fontSize }: Size) => ({
  fontSize: fontSize,
  fontWeight: 'bold',
  backgroundColor: WsColors.White,
  borderRadius: 3,
  border: '1px solid' + WsColors.TheBlues200,
  cursor: 'pointer',
  padding: 4,
}));
const StyledInput = styled(ControlledTextInput)(() => ({
  backgroundColor: WsColors.White,
  borderRadius: 3,
  border: '1px solid' + WsColors.TheBlues200,
}));

const ManageCandidateResponse = () => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [controlThree, setControlThree] = useState(false);

  const CustomTreeItem = (props: TreeItemProps) => (
    <TreeItem ContentComponent={CustomContent} {...props} />
  );
  const [showDialog, setShowDialog] = useState(false);
  const [type, setType] = useState<'reset' | 'clear'>();
  const [nodeID, setNodeID] = useState('');
  const [ediNodeName, setEdiNodeName] = useState(false);
  const [edit, setEdit] = useState('');
  const [candidateInteractionResultPipelinesData, setCandidateInteractionResultPipelinesData] =
    useState<ICandidateInteractionActionPipeline>();
  const [
    candidateInteractionResultPipelinesDataBackup,
    setCandidateInteractionResultPipelinesDataBackup,
  ] = useState<ICandidateInteractionActionPipeline>();
  const [pipelineData, setPipelineData] = useState<candidateInteractionMessagingPipelines[]>([]);
  const [idArray, setIdArray] = useState<string[]>([]);

  const methods = useForm();
  const openDialog = (type: 'reset' | 'clear') => {
    setType(type);
    setShowDialog(true);
  };
  const closeDialog = () => setShowDialog(false);
  const workspaceContext = useWorkspaceContext();
  const { access: atsCustomAccessControl } = useAtsCustomAPIAccess([
    AtsCustomAPIAccess.MANAGE_CANDIDATE_RESPONSES,
  ]);

  const manageAccess = workspaceContext.owns || atsCustomAccessControl[0];

  useEffect(() => {
    (async function () {
      const candidateInteractionResultPipelinesDataReq =
        await getCandidateInteractionResultPipelines();
      if (candidateInteractionResultPipelinesDataReq.data) {
        setCandidateInteractionResultPipelinesData(candidateInteractionResultPipelinesDataReq.data);
        setCandidateInteractionResultPipelinesDataBackup(
          candidateInteractionResultPipelinesDataReq.data
        );
      }
    })();
  }, []);
  useEffect(() => {
    if (candidateInteractionResultPipelinesData) {
      const data: candidateInteractionMessagingPipelines[] = JSON.parse(
        candidateInteractionResultPipelinesData.pipelineData
      );
      setPipelineData(data);
      setIdArray(idArrayGenerator(data));
    }
  }, [candidateInteractionResultPipelinesData]);

  const allActionsHandler = (
    array: candidateInteractionMessagingPipelines[],
    id: string,
    value: string,
    type: string
  ) => {
    const temp: candidateInteractionMessagingPipelines[] = array;
    temp.map((child, index) => {
      if (!isEmpty(child.nodes)) {
        allActionsHandler(child.nodes, id, value, type);
      }

      if (child.id === id) {
        const handler = {
          ['editName']: () => {
            child.name = value;
          },

          ['addNewChild']: () => {
            child.nodes.push(
              buildNewItem(
                `${temp[index].name}-${temp.length}`
              ) as candidateInteractionMessagingPipelines
            );
          },

          ['addNewSibling']: () => {
            temp.splice(
              index + 1,
              0,
              buildNewItem(
                `${temp[index].name}-${temp.length}`
              ) as candidateInteractionMessagingPipelines
            );
          },

          ['delete']: () => {
            temp.splice(index, 1);
          },

          ['deleteAction']: () => {
            temp[index].action = {};
          },

          ['addAction']: () => {
            temp[index].action = JSON.parse(value) as action;
          },
        };

        handler[type]();

        setPipelineData([...pipelineData]);
      }
    });
  };
  const addToTop = () => {
    pipelineData.unshift(
      buildNewItem(`New-${pipelineData.length}`) as candidateInteractionMessagingPipelines
    );
    setPipelineData([...pipelineData]);
  };

  const getTreeItemsFromData = (treeItems: candidateInteractionMessagingPipelines[]) => {
    return treeItems.map((treeItemData) => {
      let children;
      if (treeItemData.nodes && treeItemData.nodes.length > 0) {
        children = getTreeItemsFromData(treeItemData.nodes);
      }
      return (
        <CustomTreeItem
          key={treeItemData.id}
          nodeId={treeItemData.id}
          label={
            <>
              <Grid container>
                <Grid item xs={2.5}>
                  {ediNodeName && treeItemData.id === edit ? (
                    <StyledInput name="editStageName" defaultValue={treeItemData.name} />
                  ) : (
                    <SmallLabel fontSize={15}>{treeItemData.name}</SmallLabel>
                  )}
                </Grid>
                <Grid item xs={2}>
                  {ediNodeName && treeItemData.id === edit ? (
                    <SaveIconButton
                      onClick={methods.handleSubmit((data: FieldValues) => {
                        if (manageAccess) {
                          allActionsHandler(
                            pipelineData,
                            treeItemData.id,
                            data.editStageName ?? treeItemData.name,
                            'editName'
                          );

                          setEdiNodeName(false);
                          setEdit('');
                          methods.reset();
                        } else {
                          setNotification(
                            warning('You do not have enough permissions to perform this action')
                          );
                          return;
                        }
                      })}
                    />
                  ) : (
                    <EditIconButton
                      onClick={() => {
                        if (manageAccess) {
                          setEdit(treeItemData.id);
                          setEdiNodeName(true);
                        } else {
                          setNotification(
                            warning('You do not have enough permissions to perform this action')
                          );
                          return;
                        }
                      }}
                    />
                  )}
                </Grid>
                <Grid item xs={2.5}>
                  {!isEmpty(treeItemData.action) && (
                    <Stack
                      spacing={1}
                      justifyContent="space-around"
                      alignItems="center"
                      direction="row"
                    >
                      <SmallLabel fontSize={10}>{treeItemData.action?.name}</SmallLabel>
                      <DeleteIconButton
                        onClick={() =>
                          allActionsHandler(pipelineData, treeItemData.id, '', 'deleteAction')
                        }
                      />
                    </Stack>
                  )}
                </Grid>
                {manageAccess && (
                  <Grid alignItems="center" item xs={5}>
                    <Stack
                      spacing={1}
                      alignItems="center"
                      direction="row"
                      justifyContent="flex-end"
                    >
                      <WsButton
                        onClick={() => {
                          setNodeID(treeItemData.id);
                          setControlThree(true);
                        }}
                        startIcon={<AddIcon />}
                        variant="text"
                        size="small"
                      >
                        Action
                      </WsButton>
                      <WsButton
                        onClick={() =>
                          allActionsHandler(pipelineData, treeItemData.id, '', 'addNewChild')
                        }
                        startIcon={<AddIcon />}
                        variant="text"
                        size="small"
                      >
                        Child
                      </WsButton>
                      <WsButton
                        startIcon={<AddIcon />}
                        onClick={() =>
                          allActionsHandler(pipelineData, treeItemData.id, '', 'addNewSibling')
                        }
                        variant="text"
                        size="small"
                      >
                        Sibling
                      </WsButton>
                      <DeleteIcon
                        onClick={() =>
                          allActionsHandler(pipelineData, treeItemData.id, '', 'delete')
                        }
                        sx={{ fill: WsColors.Red500, cursor: 'pointer' }}
                      />
                    </Stack>
                  </Grid>
                )}
              </Grid>
            </>
          }
        >
          {children}
        </CustomTreeItem>
      );
    });
  };

  const handler = async (type: 'reset' | 'clear') => {
    if (candidateInteractionResultPipelinesData && candidateInteractionResultPipelinesDataBackup) {
      if (type === 'clear') {
        setCandidateInteractionResultPipelinesData({
          ...candidateInteractionResultPipelinesDataBackup,
        });
        setShowDialog(false);
      } else {
        try {
          const resetData = await resetDefaultCandidateInteractionResultPipelines();
          if (resetData.data) setCandidateInteractionResultPipelinesData(resetData.data);
          setShowDialog(false);
        } catch (error) {
          setNotification(warning('Something Went Wrong , Please try again'));
        }
      }
    }
  };

  const saveToApi = async () => {
    if (candidateInteractionResultPipelinesData && pipelineData) {
      const payload: ICandidateInteractionActionPipeline = candidateInteractionResultPipelinesData;
      payload.pipelineData = JSON.stringify(pipelineData);
      try {
        await saveCandidateInteractionResultPipelines(payload);
        setNotification(
          success('Configure Candidate Interaction Response,Settings updated successfully')
        );
      } catch (error) {
        setNotification(warning('Something Went Wrong , Please try again'));
      }
    }
  };
  return (
    <SettingsPageLayout
      title="Manage Candidate Response"
      indexOptions={[
        { label: 'Manage Candidate Response', url: '/candidate-interaction-result-pipeline/' },
      ]}
      breadCrumnbsList={getSettingBreadCrumbList(
        ISettingBreadCrumbTypes.CRM,
        Pages['MANAGE_CANDIDATE_RESPONSE']
      )}
    >
      <Grid margin={2}>
        {manageAccess && (
          <HeaderBox
            title="Candidate Interaction Action Pipeline"
            includeBtn={true}
            btnText="Save Everything"
            onClick={() => saveToApi()}
          />
        )}
        {manageAccess && (
          <Stack mb={2} justifyContent="end" display="flex" direction="row" spacing={2}>
            <WsButton size="small" onClick={() => addToTop()} variant="contained">
              Add New Item
            </WsButton>
            <WsButton size="small" onClick={() => openDialog('clear')} variant="contained">
              Clear Unsaved Changes
            </WsButton>
            <WsButton size="small" onClick={() => openDialog('reset')} variant="contained">
              Reset to Default
            </WsButton>
          </Stack>
        )}
        <FormProvider {...methods}>
          <Grid margin={2}>
            <TreeView
              expanded={idArray}
              defaultExpandIcon={<ChevronRightIcon />}
              defaultCollapseIcon={<ExpandMoreIcon />}
            >
              {getTreeItemsFromData(pipelineData)}
            </TreeView>
          </Grid>
        </FormProvider>
        <AddActionButtonDialog
          openDialog={controlThree}
          closeDialog={() => setControlThree(false)}
          onChangeHandler={(data: action) =>
            allActionsHandler(pipelineData, nodeID, data as string, 'addAction')
          }
        />
        <ConfirmationDialog
          openDialog={showDialog}
          closeDialog={() => closeDialog()}
          handler={(type) => handler(type)}
          variant={type ?? 'clear'}
        />
      </Grid>
    </SettingsPageLayout>
  );
};
export default ManageCandidateResponse;
